/**
 * Cockpit visual effects — confetti burst and floating reward toast.
 *
 * Port of the handoff's `design/fx.js` to an ES module. Used only by the
 * collectible card cockpit pages; nothing else in the app imports this.
 */

/** Every cockpit animation is gated behind this check (handoff: CE_FX.reduce). */
export const reduceMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Degen confetti burst: a shockwave ring at the origin, then a canvas burst
 * from three cannons (origin + both bottom corners) across six particle types.
 *
 * @param {number} originX - Viewport x to burst from (usually the CTA centre)
 * @param {number} originY - Viewport y to burst from
 * @param {{colors?: string[], emojis?: string[], count?: number}} [opts]
 */
export function fireConfetti(originX, originY, opts = {}) {
  if (reduceMotion()) return;

  const colors = opts.colors || ["#2E6BFF", "#3B9EFF", "#FFCB05", "#FFE066", "#16B57F", "#E64646"];
  const emojis = opts.emojis || ["💎", "🚀", "🔥", "⚡", "🤑"];
  const count = opts.count || 210;

  // shockwave ring at the origin
  const ring = document.createElement("div");
  ring.style.cssText = `position:fixed;left:${originX}px;top:${originY}px;width:20px;height:20px;margin:-10px 0 0 -10px;border-radius:50%;pointer-events:none;z-index:99999;border:3px solid ${colors[0]};box-shadow:0 0 30px ${colors[1]}`;
  document.body.appendChild(ring);
  ring.animate(
    [
      { transform: "scale(0.2)", opacity: 0.85, borderWidth: "4px" },
      { transform: "scale(14)", opacity: 0, borderWidth: "0.5px" },
    ],
    { duration: 620, easing: "cubic-bezier(.15,.6,.3,1)" }
  ).onfinish = () => ring.remove();

  const canvas = document.createElement("canvas");
  canvas.style.cssText = "position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:99999";
  const dpr = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);
  document.body.appendChild(canvas);

  // three launch points: centre burst + two side cannons from the bottom
  const sources = [
    { x: originX, y: originY, spread: Math.PI * 1.1, aim: -Math.PI / 2, smin: 7, smax: 20, n: 0.5 },
    { x: 40, y: window.innerHeight - 20, spread: Math.PI * 0.42, aim: -Math.PI / 2.7, smin: 16, smax: 30, n: 0.25 },
    { x: window.innerWidth - 40, y: window.innerHeight - 20, spread: Math.PI * 0.42, aim: -Math.PI + Math.PI / 2.7, smin: 16, smax: 30, n: 0.25 },
  ];
  const pick = (a) => a[(Math.random() * a.length) | 0];
  const parts = [];
  for (const s of sources) {
    const nn = Math.round(count * s.n);
    for (let i = 0; i < nn; i++) {
      const angle = s.aim + (Math.random() - 0.5) * s.spread;
      const speed = s.smin + Math.random() * (s.smax - s.smin);
      const r = Math.random();
      const shape =
        r < 0.42 ? "rect" : r < 0.6 ? "circle" : r < 0.78 ? "streamer" : r < 0.9 ? "star" : r < 0.965 ? "spark" : "emoji";
      parts.push({
        x: s.x,
        y: s.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: shape === "emoji" ? 16 + Math.random() * 12 : 5 + Math.random() * 9,
        color: pick(colors),
        glyph: pick(emojis),
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.6,
        wob: Math.random() * Math.PI * 2,
        wobSpd: 0.1 + Math.random() * 0.18,
        wobAmp: 0.4 + Math.random() * 1.6,
        life: 0,
        ttl: 95 + Math.random() * 75,
        shape,
      });
    }
  }

  const drawStar = (rad) => {
    ctx.beginPath();
    for (let i = 0; i < 10; i++) {
      const a = (Math.PI / 5) * i - Math.PI / 2;
      const rr = i % 2 ? rad * 0.45 : rad;
      ctx[i ? "lineTo" : "moveTo"](Math.cos(a) * rr, Math.sin(a) * rr);
    }
    ctx.closePath();
    ctx.fill();
  };

  const tick = () => {
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    let alive = false;
    for (const p of parts) {
      p.life++;
      if (p.life > p.ttl) continue;
      alive = true;
      p.wob += p.wobSpd;
      p.vy += 0.3;          // gravity
      p.vx *= 0.985;        // drag
      p.vy *= 0.995;
      p.x += p.vx + Math.cos(p.wob) * p.wobAmp;
      p.y += p.vy;
      p.rot += p.vr;
      const fade = Math.max(0, 1 - p.life / p.ttl);
      ctx.save();
      ctx.translate(p.x, p.y);
      if (p.shape === "spark") {
        ctx.globalAlpha = fade * (0.5 + 0.5 * Math.abs(Math.sin(p.wob * 2)));
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2.4, 0, 7);
        ctx.fill();
      } else if (p.shape === "emoji") {
        ctx.globalAlpha = fade;
        ctx.rotate(p.rot);
        ctx.font = `${p.size}px serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(p.glyph, 0, 0);
      } else {
        ctx.globalAlpha = fade;
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === "rect") ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        else if (p.shape === "streamer") ctx.fillRect(-p.size / 6, -p.size * 1.4, p.size / 3, p.size * 2.8);
        else if (p.shape === "star") drawStar(p.size * 0.8);
        else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, 7);
          ctx.fill();
        }
      }
      ctx.restore();
    }
    if (alive) requestAnimationFrame(tick);
    else canvas.remove();
  };
  tick();
}

/**
 * Floating reward pill — rises from the centre and fades ("+120 XP · …").
 * @param {string} text - Headline, e.g. "+120 XP"
 * @param {string} [sub] - Secondary line
 */
export function rewardToast(text, sub) {
  const el = document.createElement("div");
  el.style.cssText = [
    "position:fixed",
    "left:50%",
    "top:22%",
    "transform:translate(-50%,0)",
    "z-index:99998",
    "pointer-events:none",
    "text-align:center",
    'font-family:"Sora", system-ui, sans-serif',
    "padding:16px 28px",
    "border-radius:20px",
    "background:linear-gradient(135deg, rgba(46,107,255,0.94), rgba(59,158,255,0.88))",
    "box-shadow:0 20px 60px rgba(46,107,255,0.5), inset 0 1px 0 rgba(255,203,5,0.35)",
    "backdrop-filter:blur(8px)",
    "opacity:0",
  ].join(";");

  const head = document.createElement("div");
  head.style.cssText =
    "font-size:26px;font-weight:800;color:#fff;letter-spacing:-0.01em;text-shadow:0 2px 10px rgba(0,0,0,0.25)";
  head.textContent = text;
  el.appendChild(head);
  if (sub) {
    const line = document.createElement("div");
    line.style.cssText = "font-size:13px;font-weight:600;color:rgba(255,255,255,0.9);margin-top:2px";
    line.textContent = sub;
    el.appendChild(line);
  }

  document.body.appendChild(el);
  if (reduceMotion()) {
    el.style.opacity = "1";
    setTimeout(() => el.remove(), 1400);
    return;
  }
  el.animate(
    [
      { opacity: 0, transform: "translate(-50%,20px) scale(0.85)" },
      { opacity: 1, transform: "translate(-50%,-6px) scale(1.05)", offset: 0.25 },
      { opacity: 1, transform: "translate(-50%,-14px) scale(1)", offset: 0.6 },
      { opacity: 0, transform: "translate(-50%,-70px) scale(0.95)" },
    ],
    { duration: 1900, easing: "cubic-bezier(.22,1,.36,1)" }
  ).onfinish = () => el.remove();
}

/**
 * Darken (amt < 0) or lighten (amt > 0) a hex colour. Handoff: `shade`.
 * @param {string} hex - "#rgb" or "#rrggbb"
 * @param {number} amt - -1..1
 * @returns {string} rgb() string
 */
export function shade(hex, amt) {
  let c = String(hex || "#888888").replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const f = (v) => Math.max(0, Math.min(255, Math.round(amt < 0 ? v * (1 + amt) : v + (255 - v) * amt)));
  return `rgb(${f(r)},${f(g)},${f(b)})`;
}
